'use client';

import { useEffect, useState } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { extractJSON, run } from '@/lib/ai';
import { decisionOptions } from '@/lib/prompts';
import { Nav, StepHead, StepWrap } from '../StepShell';
import { Textarea } from '../Textarea';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

type Option = { id: string; label: string; desc: string };

const STATIC_FALLBACK: Option[] = [
  { id: '0', label: 'Product or design decisions', desc: 'Inform feature development, UX improvements, or product strategy' },
  { id: '1', label: 'Academic publication', desc: 'Contribute to scholarly research or a thesis' },
  { id: '2', label: 'Policy or organizational decisions', desc: 'Shape internal policies, guidelines, or institutional strategy' },
  { id: '3', label: 'Business strategy', desc: 'Guide go-to-market, positioning, or customer understanding' },
];

export function Step2Decision({ data, setData, onBack, onNext }: Props) {
  const selected = data.decision || '';
  const custom = data.decisionCustom || '';
  // If there's no goal we have nothing to ask the AI about — start with the
  // static fallback so the effect below doesn't need a synchronous setState.
  const [options, setOptions] = useState<Option[] | null>(
    data.goal ? null : STATIC_FALLBACK,
  );
  const [aiError, setAiError] = useState(false);

  useEffect(() => {
    if (!data.goal) return;
    run(decisionOptions(data.goal))
      .then((text) => {
        const parsed = extractJSON<Array<{ label: string; desc: string }>>(text);
        if (parsed) {
          setOptions(parsed.map((o, i) => ({ id: String(i), label: o.label, desc: o.desc })));
        } else {
          setAiError(true);
          setOptions(STATIC_FALLBACK);
        }
      })
      .catch(() => {
        setAiError(true);
        setOptions(STATIC_FALLBACK);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allOptions: Option[] = options
    ? [...options, { id: 'other', label: 'Something else', desc: 'Describe your own purpose below' }]
    : [];

  const select = (id: string) =>
    setData({ ...data, decision: id, decisionCustom: id !== 'other' ? '' : custom });

  const canContinue = !!selected && (selected !== 'other' || custom.trim().length > 0);

  return (
    <StepWrap>
      <StepHead
        label="Step 3 — Research Purpose"
        title="How do you intend to use the findings?"
        subtitle="Knowing how you intend to use the findings helps the AI steer the interview in the right direction — including what to probe and follow up on. Choose the one that best describes your primary intention — you'll be able to provide more context later."
      />

      {!options && !aiError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: 72,
                borderRadius: 12,
                background: T.creamMid,
                animation: 'pulse 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
          <div style={{ fontSize: 13, color: T.espressoLight, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>✦</span>
            Generating options based on your study goal…
          </div>
        </div>
      )}

      {options && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!aiError && (
            <div style={{ fontSize: 12, color: T.espressoLight, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <span style={{ color: T.terra }}>✦</span> Options tailored to your study goal
            </div>
          )}
          {allOptions.map((opt) => {
            const on = selected === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => select(opt.id)}
                style={{
                  border: `1.5px solid ${on ? T.terra : T.creamBorder}`,
                  borderRadius: 12,
                  padding: '16px 20px',
                  background: on ? T.terraLight : T.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    flexShrink: 0,
                    marginTop: 2,
                    border: `2px solid ${on ? T.terra : T.creamBorder}`,
                    background: on ? T.terra : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {on && <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.white }} />}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: T.espresso, marginBottom: 2 }}>{opt.label}</div>
                  <div style={{ fontSize: 13, color: T.espressoLight, lineHeight: 1.5 }}>{opt.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected === 'other' && (
        <Textarea
          value={custom}
          onChange={(v) => setData({ ...data, decisionCustom: v })}
          placeholder="Describe how you plan to use the findings from this study…"
          rows={3}
          label="Describe your purpose"
        />
      )}

      <Nav onBack={onBack} onNext={onNext} nextDisabled={!canContinue} />
    </StepWrap>
  );
}
